import SvgIcon from "@/components/common/SvgIcon";
import { SelectBox } from "@/components/kit";
import { IconButton } from "@material-tailwind/react";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "..";

type TProps = {
  totalPages: number;
  limit: number;
  activePage: number;
  totalItems?: number;
  onSetActivePage: (pageIndex: number) => void;
  onSelectLimit: (limit: number) => void;
};

const limitList = [25, 50, 100];

function Pagination({
  limit,
  totalPages,
  activePage,
  totalItems,
  onSetActivePage,
  onSelectLimit,
}: Readonly<TProps>) {
  const { t } = useTranslation();

  const pages = useMemo(() => [...Array(totalPages).keys()], [totalPages]);

  function handleSetActiveTab(pageIndex: number) {
    if (activePage === pageIndex) return;
    onSetActivePage(pageIndex);
  }

  function handleSelectLimit(pageIndex: number) {
    if (activePage === pageIndex) return;
    onSetActivePage(1);
    onSelectLimit(pageIndex);
  }

  if (totalPages === 0) return <></>;

  function renderButton(start: number, end: number) {
    return (
      <Fragment>
        {pages.slice(start, end).map((item) => (
          <Button
            variant="text"
            key={`${item}_pagination`}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleSetActiveTab(item + 1)}
          >
            <Typography
              variant="Body2-Bold"
              color={
                activePage === item + 1 ? "text-gray-800" : "text-gray-300"
              }
            >
              {item + 1}
            </Typography>
          </Button>
        ))}
      </Fragment>
    );
  }

  function renderPages() {
    if (totalPages > 6) {
      if (activePage < 3)
        return (
          <Fragment>
            {renderButton(0, 3)}
            <Typography
              color="text-gray-300"
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(totalPages)}
            >
              <Typography
                variant="Body2-Bold"
                color={
                  activePage === totalPages ? "text-gray-800" : "text-gray-300"
                }
              >
                {totalPages}
              </Typography>
            </Button>
          </Fragment>
        );
      if (activePage === 3)
        return (
          <Fragment>
            {renderButton(0, 4)}
            <Typography
              color="text-gray-300"
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(totalPages)}
            >
              <Typography
                variant="Body2-Bold"
                color={
                  activePage === totalPages ? "text-gray-800" : "text-gray-300"
                }
              >
                {totalPages}
              </Typography>
            </Button>
          </Fragment>
        );
      if (activePage >= 4 && activePage <= totalPages - 3)
        return (
          <Fragment>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(1)}
            >
              <Typography
                variant="Body2-Bold"
                color={
                  activePage === totalPages ? "text-gray-800" : "text-gray-300"
                }
              >
                1
              </Typography>
            </Button>
            <Typography
              color="text-gray-300"
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            {renderButton(activePage - 2, activePage + 1)}
            <Typography
              color="text-gray-300 "
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(totalPages)}
            >
              <Typography
                variant="Body2-Bold"
                color={
                  activePage === totalPages ? "text-gray-800" : "text-gray-300"
                }
              >
                {totalPages}
              </Typography>
            </Button>
          </Fragment>
        );
      if (activePage === totalPages - 2)
        return (
          <Fragment>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(1)}
            >
              <Typography
                variant="Body2-Bold"
                color={activePage === 1 ? "text-gray-800" : "text-gray-300"}
              >
                1
              </Typography>
            </Button>
            <Typography
              color="text-gray-300"
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            {renderButton(totalPages - 4, totalPages)}
          </Fragment>
        );
      if (activePage >= totalPages - 3)
        return (
          <Fragment>
            <Button
              variant="text"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleSetActiveTab(1)}
            >
              <Typography
                variant="Body2-Bold"
                color={activePage === 1 ? "text-gray-800" : "text-gray-300"}
              >
                1
              </Typography>
            </Button>
            <Typography
              color="text-gray-300"
              className="p-2"
              variant="Body2-Bold"
            >
              ...
            </Typography>
            {renderButton(totalPages - 3, totalPages)}
          </Fragment>
        );
    }

    return pages.map((_item, index) => (
      <Button
        variant="text"
        key={`${index}_pagination`}
        onClick={() => handleSetActiveTab(index + 1)}
      >
        <Typography
          variant="Body2-Bold"
          color={activePage === index + 1 ? "text-gray-800" : "text-gray-300"}
        >
          {index + 1}
        </Typography>
      </Button>
    ));
  }

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-4">
      <div className="flex justify-between items-center w-full md:w-auto px-2 md:px-0">
        <SelectBox
          arrowClassName="!border-0"
          className="!w-32 !h-8"
          value={limit}
          renderValue={(value) => (
            <div className="flex gap-2">
              <Typography variant="Body2-Regular">
                {`${t("Show")} ${value}`}
              </Typography>
            </div>
          )}
          renderOption={(value) => (
            <Typography variant="Body2-Regular">
              {`${t("Show")} ${value}`}
            </Typography>
          )}
          options={limitList}
          onChange={(e) => handleSelectLimit(e)}
          fixedDropdown={true}
          dropdownZIndex={9999}
        />
        {!!totalItems && (
          <Typography variant="Body2-Regular" className="md:hidden">
            {`${(activePage - 1) * limit + 1}-${Math.min(activePage * limit, totalItems)} of ${totalItems}`}
          </Typography>
        )}
      </div>

      {!!totalItems && (
        <Typography variant="Body2-Regular" className="hidden md:block">
          {`${(activePage - 1) * limit + 1}-${Math.min(activePage * limit, totalItems)} of ${totalItems}`}
        </Typography>
      )}

      <div className="flex justify-center md:flex-row items-center gap-4 w-full md:w-auto">
        <IconButton
          placeholder=""
          className="w-8 h-8 border-gray-200"
          size="sm"
          variant="outlined"
          disabled={activePage === 1}
          onClick={() => onSetActivePage(activePage - 1)}
        >
          <SvgIcon
            iconName="arrowRight"
            className="rotate-180"
            fill="fill-gray-600"
          />
        </IconButton>
        <div className="flex  md:flex-row items-center gap-2">
          {renderPages()}
        </div>
        <IconButton
          placeholder=""
          className="w-8 h-8 border-gray-200"
          size="sm"
          variant="outlined"
          disabled={activePage === totalPages}
          onClick={() => onSetActivePage(activePage + 1)}
        >
          <SvgIcon iconName="arrowRight" fill="fill-gray-600" />
        </IconButton>
      </div>
    </div>
  );
}

export default Pagination;
